import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { App as AntdApp, ConfigProvider } from "antd";
import he_IL from "antd/locale/he_IL";
import en_US from "antd/locale/en_US";
import { hrAntdTheme } from "./design-system/hrAntdTheme";
import InnerRoutes from "./Routes/Routes";
import "./App.css";
import "./Pages/Admin/adminDashboardLayout.scss";
import "./styles/mobile-full-width-layout.css";
import "./styles/responsive-shell.css";
import { SearchProvider } from "./contexts/SearchContext";
import { SelectedResultProvider } from "./contexts/SelectedResultContext";
import { TimerProvider } from "./contexts/TimerContext";
import { OrderProvider } from "./contexts/OrderContext";
import { GoogleReviewsProvider } from "./contexts/GoogleReviewsContext";
import { useAppContext } from "./contexts/AppContext";
import { Loader } from "./Components/Loader/Loader";
import TripMasterAccessGate from "./features/vacationPackagesPublic/TripMasterAccessGate";
import { CLIENT_ID } from "./assets/GlobalVariables";
import AppToastHost from "./utils/appToast/AppToastHost";
import ScrollToTopButton from "./Components/Common/ScrollToTopButton/ScrollToTopButton";
import { DevViewModeProvider } from "./dev/viewMode";
import { getUiDirection, setUiDirection, subscribeUiDirection } from "./i18n/uiDirection";

const PERF_ISOLATION = false;
const PERF_ISOLATION_STAGE = "FULL_TREE";
// STATIC | WITHOUT_SELECTED_RESULT_PROVIDER | WITH_SELECTED_RESULT_STUB | FULL_TREE
// Inner App component that uses AppContext
const AppContent = () => {
  const { currencyRate } = useAppContext();
  const { i18n } = useTranslation();
  const [uiDir, setUiDir] = React.useState(getUiDirection);

  if (typeof window !== "undefined") {
    window.__PERF_ISOLATION__ = PERF_ISOLATION;
    window.__PERF_ISOLATION_STAGE__ = PERF_ISOLATION_STAGE;
    window.__PERF_SELECTED_RESULT_STUB__ =
      PERF_ISOLATION &&
      PERF_ISOLATION_STAGE === "WITH_SELECTED_RESULT_STUB";
  }

  if (PERF_ISOLATION && PERF_ISOLATION_STAGE === "STATIC") {
    return <div style={{ padding: 24 }}>PERF ISOLATION OK</div>;
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__appBuildTag = "APP-2026-01-18-1";
    }
  }, []);

  useEffect(() => {
    // Suppress findDOMNode deprecation warnings from antd (will be fixed in future antd versions)
    // Also suppress Google Maps PlacesService deprecation warning (we'll migrate later)
    const originalWarn = console.warn;
    const originalError = console.error;
    console.warn = (...args) => {
      const warningMessage = args[0]?.toString() || '';
      if (
        warningMessage.includes("findDOMNode is deprecated") ||
        warningMessage.includes("Warning: findDOMNode") ||
        warningMessage.includes("google.maps.places.PlacesService is not available") ||
        warningMessage.includes("google.maps.places.Place instead") ||
        warningMessage.includes("loading=async") ||
        warningMessage.includes("google.maps.Marker is deprecated")
      ) {
        return;
      }
      originalWarn.apply(console, args);
    };
    console.error = (...args) => {
      const errorMessage = args.map((a) => String(a ?? "")).join(" ");
      if (
        errorMessage.includes("LegacyApiNotActivatedMapError") ||
        errorMessage.includes("Google Maps JavaScript API error: LegacyApiNotActivatedMapError")
      ) {
        return;
      }
      if (
        !import.meta.env?.DEV &&
        errorMessage.includes("[HOTEL_ONLY_ORDER_RESET_TRACE]")
      ) {
        return;
      }
      originalError.apply(console, args);
    };

    return () => {
      console.warn = originalWarn;
      console.error = originalError;
    };
  }, []);

  // Direction follows URL locale (/en /el → LTR). i18n stays Hebrew-only.
  // document lang stays Hebrew + notranslate to keep Chrome / Google Translate off the product UI.
  const antdLocale = uiDir === "rtl" ? he_IL : en_US;

  useEffect(() => subscribeUiDirection(setUiDir), []);

  useEffect(() => {
    const pathDir = /^\/(en|el)(\/|$)/.test(window.location.pathname) ? "ltr" : getUiDirection();
    setUiDirection(pathDir);
    document.documentElement.setAttribute("lang", "he");
    document.documentElement.setAttribute("translate", "no");
  }, [i18n]);

  if (!currencyRate.USD) {
    return (
      <TripMasterAccessGate>
        <Loader />
      </TripMasterAccessGate>
    );
  }

  let content = (
    <TimerProvider>
      <SearchProvider>
        <SelectedResultProvider>
          <OrderProvider>
            <GoogleReviewsProvider>
              <InnerRoutes />
            </GoogleReviewsProvider>
          </OrderProvider>
        </SelectedResultProvider>
      </SearchProvider>
    </TimerProvider>
  );

  if (PERF_ISOLATION && PERF_ISOLATION_STAGE === "WITHOUT_SELECTED_RESULT_PROVIDER") {
    content = (
      <TimerProvider>
        <SearchProvider>
          <OrderProvider>
            <GoogleReviewsProvider>
              <InnerRoutes />
            </GoogleReviewsProvider>
          </OrderProvider>
        </SearchProvider>
      </TimerProvider>
    );
  }

  if (PERF_ISOLATION && PERF_ISOLATION_STAGE === "WITH_SELECTED_RESULT_STUB") {
    content = (
      <TimerProvider>
        <SearchProvider>
          <SelectedResultProvider perfStub>
            <OrderProvider>
              <GoogleReviewsProvider>
                <InnerRoutes />
              </GoogleReviewsProvider>
            </OrderProvider>
          </SelectedResultProvider>
        </SearchProvider>
      </TimerProvider>
    );
  }

  // WARNING: KEEP SelectedResultProvider INSIDE SearchProvider when FULL_TREE stage is active
  return (
    <GoogleOAuthProvider clientId={CLIENT_ID}>
      <DevViewModeProvider>
      <ConfigProvider locale={antdLocale} direction={uiDir} theme={hrAntdTheme}>
        <AntdApp
          /* height:100% יוצר מיכל בגובה viewport ושובר position:sticky מול גלילת החלון */
          style={{ minHeight: "100%" }}
          notification={{
            placement: "topRight",
            duration: 4.5,
            showProgress: true,
            pauseOnHover: true,
          }}
        >
          <TripMasterAccessGate>{content}</TripMasterAccessGate>
          <AppToastHost />
          <ScrollToTopButton />
        </AntdApp>
      </ConfigProvider>
      </DevViewModeProvider>
    </GoogleOAuthProvider>
  );
};

// AppProvider מוגדר ב-index.js (מקור יחיד) — כדי לא לכפול סטייט ולא לשבור טסטים/ייבוא
const App = () => <AppContent />;

export default App;
